/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Spinner, Stack } from 'component-library';
import React, { HTMLAttributes } from 'react';

import { WidgetSlot } from '@/api/rest/resources/types/widget';
import { SuspenseBoundary } from '@/components';
import { useWidgetByNameType } from '@/hooks/useWidgetByNameType';
import { createDefaultWidgetFromSlot } from '@/pages/shared/hooks/useDefaultWidgetProps';
import { isNullish } from '@/utils/booleans';

// eslint-disable-next-line import/no-cycle
import { WIDGET_TYPE_COMPONENT_MAP } from './constants';
import { WidgetErrorBoundary } from './ErrorBoundary';
import { useWidgetDataContext, WidgetContext } from './WidgetContext';

type WidgetProps = HTMLAttributes<HTMLDivElement> & Pick<WidgetSlot, 'name' | 'type' | 'relatedData'>;

const WidgetBase = ({ name, type, relatedData, children, ...delegated }: WidgetProps) => {
  let widget = useWidgetByNameType(name, type);

  if (isNullish(widget)) {
    widget = createDefaultWidgetFromSlot(name, type, relatedData);
  }

  widget = {
    ...widget,
    widget_format: {
      ...widget.widget_format,
      ...relatedData,
    },
  };
  const WidgetComponent = WIDGET_TYPE_COMPONENT_MAP[`${type}` as keyof typeof WIDGET_TYPE_COMPONENT_MAP];
  if (!WidgetComponent) return null;

  return (
    <WidgetContext widget={widget}>
      <WidgetWithContext WidgetComponent={WidgetComponent} delegated={delegated}>
        {children}
      </WidgetWithContext>
    </WidgetContext>
  );
};

export const Widget = ({ name, type, relatedData, children, ...delegated }: WidgetProps) => {
  return (
    <WidgetErrorBoundary name={name} type={type}>
      <WidgetBase name={name} type={type} relatedData={relatedData} {...delegated}>
        {children}
      </WidgetBase>
    </WidgetErrorBoundary>
  );
};

const WidgetWithContext = ({
  WidgetComponent,
  children,
  delegated,
}: {
  WidgetComponent: React.ComponentType<any>;
  children?: React.ReactNode;
  delegated?: any;
}) => {
  const context = useWidgetDataContext();

  if (!context) return null;

  const { widget, data } = context;
  const { widget_format = {} } = widget || {};

  return (
    <SuspenseBoundary
      fallback={
        <Stack centerMain>
          <Spinner size={10} />
        </Stack>
      }
    >
      <WidgetComponent {...widget_format} data={data} {...delegated}>
        {children}
      </WidgetComponent>
    </SuspenseBoundary>
  );
};
